import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from './Notification';
import { theme } from '../theme';
import { RankingResponse } from '../types';
import { logger } from '../utils/logger';
import { useResponsive } from '../hooks/useResponsive';


interface RankingsProps {
  rankings: RankingResponse[];
  isVisible?: boolean;
  onClose?: () => void;
}

type RankingType = 'rating' | 'reviews' | 'positive' | 'activity';

export const Rankings: React.FC<RankingsProps> = ({
  rankings: propRankings,
  isVisible = true,
  onClose
}) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { notification, showNotification, hideNotification } = useNotification();
  
  const [rankings, setRankings] = useState<RankingResponse[]>(propRankings || []);
  const [selectedType, setSelectedType] = useState<RankingType>('rating');
  const [loading, setLoading] = useState(false);


  const rankingTypes = [
    { type: 'rating', title: '🏆 Mejor Rating', description: 'Jugadores con mayor calificación' },
    { type: 'reviews', title: '⭐ Más Reviews', description: 'Jugadores con más reviews recibidas' },
    { type: 'positive', title: '👍 Más Positivas', description: 'Jugadores con mejor balance positivo' },
    { type: 'activity', title: '⚽ Más Activos', description: 'Jugadores que más partidos juegan' }
  ];

  useEffect(() => {
    if (isVisible && (!propRankings || propRankings.length === 0)) {
      fetchRankings();
    } else if (propRankings) {
      setRankings(propRankings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, propRankings]);

  const fetchRankings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/gamification/rankings'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRankings(data.rankings);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando rankings', 'error');
      }
    } catch (error) {
      logger.error('Error fetching rankings:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentRanking = () => {
    return rankings.find(r => r.type === selectedType);
  };

  const getPositionStyle = (position: number) => {
    if (position === 1) {
      return {
        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
        color: '#000',
        fontWeight: 'bold' as const,
        boxShadow: '0 4px 15px rgba(255, 215, 0, 0.4)'
      };
    } else if (position === 2) {
      return {
        background: 'linear-gradient(135deg, #C0C0C0, #A0A0A0)',
        color: '#000',
        fontWeight: 'bold' as const,
        boxShadow: '0 4px 15px rgba(192, 192, 192, 0.4)'
      };
    } else if (position === 3) {
      return {
        background: 'linear-gradient(135deg, #CD7F32, #B8860B)',
        color: '#000',
        fontWeight: 'bold' as const,
        boxShadow: '0 4px 15px rgba(205, 127, 50, 0.4)'
      };
    }
    return {
      background: 'linear-gradient(135deg, #2d2d2d, #1a1a1a)',
      color: theme.colors.text,
      border: '1px solid #333'
    };
  };

  const getMedalIcon = (position: number) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return `${position}`;
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '32px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: isMobile ? '16px' : theme.spacing.lg,
        maxWidth: isMobile ? '100%' : '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`,
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg
        }}>
            <h2 style={{
              margin: 0,
              color: '#FFD700',
              fontFamily: theme.fonts.heading,
              fontSize: isMobile ? '20px' : theme.fonts.sizeTitle,
              textShadow: '0 0 10px #FFD700',
              background: 'linear-gradient(45deg, #FFD700, #FFA500)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              🏆 Rankings de Jugadores
            </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: theme.colors.textSecondary
            }}
          >
            ×
          </button>
        </div>

        {/* Selector de tipo de ranking */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: theme.spacing.sm,
          marginBottom: theme.spacing.lg
        }}>
          {rankingTypes.map((rankingType) => (
            <button
              key={rankingType.type}
              onClick={() => setSelectedType(rankingType.type as RankingType)}
              style={{
                padding: theme.spacing.md,
                borderRadius: theme.borderRadius.card,
                border: selectedType === rankingType.type ? '2px solid #FFD700' : '1px solid #333',
                background: selectedType === rankingType.type 
                  ? 'linear-gradient(135deg, #FFD70020, #FFA50020)' 
                  : 'linear-gradient(135deg, #2d2d2d, #1a1a1a)',
                color: selectedType === rankingType.type ? '#FFD700' : theme.colors.text,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left' as const,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeSmall
              }}
              onMouseEnter={(e) => {
                if (selectedType !== rankingType.type) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedType !== rankingType.type) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div style={{ fontWeight: theme.fonts.weightBold, marginBottom: '4px' }}>
                {rankingType.title}
              </div>
              <div style={{ 
                color: selectedType === rankingType.type ? '#FFD700' : theme.colors.textSecondary,
                fontSize: '12px'
              }}>
                {rankingType.description}
              </div>
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
            <div style={{
              fontSize: '24px',
              animation: 'spin 1s linear infinite'
            }}>
              ⚽
            </div>
            <p style={{ color: theme.colors.textSecondary }}>Cargando rankings...</p>
          </div>
        ) : (
          <div>
            {getCurrentRanking() && (
              <div style={{
                textAlign: 'center',
                marginBottom: theme.spacing.lg,
                padding: theme.spacing.md,
                background: 'linear-gradient(135deg, #FFD70020, #FFA50020)',
                borderRadius: theme.borderRadius.card,
                border: '1px solid #FFD70040'
              }}>
                <h3 style={{
                  margin: '0 0 8px 0',
                  color: '#FFD700',
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fonts.sizeBody
                }}>
                  {getCurrentRanking()?.title}
                </h3>
                <p style={{
                  margin: 0,
                  color: theme.colors.textSecondary,
                  fontSize: theme.fonts.sizeSmall
                }}>
                  Total de jugadores: {getCurrentRanking()?.total}
                </p>
              </div>
            )}

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing.sm
            }}>
              {getCurrentRanking()?.ranking.map((player, index) => (
                <div
                  key={player.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: theme.spacing.md,
                    borderRadius: theme.borderRadius.card,
                    ...getPositionStyle(index + 1),
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  {/* Posición */}
                  <div style={{
                    width: '50px',
                    textAlign: 'center',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginRight: theme.spacing.md
                  }}>
                    {getMedalIcon(index + 1)}
                  </div>

                  {/* Avatar y nombre */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    gap: theme.spacing.md
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}>
                      {player.avatar || player.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{
                        fontWeight: theme.fonts.weightBold,
                        fontSize: theme.fonts.sizeBody,
                        marginBottom: '2px'
                      }}>
                        {player.nickname || player.name}
                      </div>
                      <div style={{
                        color: theme.colors.textSecondary,
                        fontSize: theme.fonts.sizeSmall
                      }}>
                        {player.name}
                      </div>
                    </div>
                  </div>

                  {/* Estadísticas */}
                  <div style={{
                    display: 'flex',
                    gap: theme.spacing.lg,
                    alignItems: 'center'
                  }}>
                    {selectedType === 'rating' && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                          {player.rating}
                        </div>
                        <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                          Rating
                        </div>
                      </div>
                    )}

                    {selectedType === 'reviews' && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                          {player.totalReviews || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                          Reviews
                        </div>
                      </div>
                    )}

                    {selectedType === 'positive' && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                          {player.positivePercentage || '0%'}
                        </div>
                        <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                          Positivas
                        </div>
                      </div>
                    )}

                    {selectedType === 'activity' && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                          {player._count?.userMatchHistory || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                          Partidos
                        </div>
                      </div>
                    )}

                    {/* Indicador de usuario actual */}
                    {user?.id === player.id && (
                      <div style={{
                        background: '#FFD700',
                        color: '#000',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        TÚ
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Botón cerrar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.spacing.lg
        }}>
          <button
            onClick={onClose}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              color: '#000',
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: theme.fonts.weightBold,
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.3)';
            }}
          >
            Cerrar
          </button>
        </div>

        {/* Notificación */}
        {notification.isVisible && (
          <button type="button" onClick={hideNotification} style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            zIndex: 1001,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: notification.type === 'success' ? theme.colors.success :
                           notification.type === 'error' ? theme.colors.danger :
                           notification.type === 'warning' ? theme.colors.warning : theme.colors.primary
          }}>
            <span>{notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : notification.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
            <span>{notification.message}</span>
          </button>
        )}


      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}; 